import {metadaApi} from '@dms/api/base';

import {
  type DailyStockTakeDetailResponse,
  DailyStockTakeDetailResponseSchema,
} from './DailyStockTakeDetailResponseSchema';

/**
 * The daily stock take detail is served by the metada `report` module, so this endpoint injects
 * into `metadaApi` while the rest of this lib's endpoints (`api.ts`) go to `promptBridgeApi`.
 * The split is deliberate — do not move this into `api.ts`.
 *
 * The module is mounted at `report/v1` and its operation address repeats `v1/report`, so the
 * served path really is `/report/v1/v1/report/daily-stock-take/{id}`. The doubling is not a typo;
 * `hyundai_omnetic/v1/v1/...` in `@dms/api/workshop` has the same shape for the same reason.
 * See the module's OpenAPI document: `teas/api/report/v1/openapi.json`, operation
 * `RPT_get_dailyStockTakeById_API`.
 */
const dailyStockTakeDetailUrl = (id: string) =>
  `/report/v1/v1/report/daily-stock-take/${encodeURIComponent(id)}`;

const dailyStockTakeEndpoints = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getDailyStockTakeById: build.query<DailyStockTakeDetailResponse, string>({
      query: (id) => ({url: dailyStockTakeDetailUrl(id)}),
      /**
       * No `providesTags`: a daily closing is an immutable snapshot of a finished day, and
       * `metadaApi` has no tag type that would fit it. Adding one would mean editing the shared
       * base API for a single read-only detail.
       */
      extraOptions: {responseSchema: DailyStockTakeDetailResponseSchema},
    }),
  }),
});

// Named rather than destructured so the export is visible to `eag(match-named-export)`.
export const useGetDailyStockTakeByIdQuery = dailyStockTakeEndpoints.useGetDailyStockTakeByIdQuery;
